import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  FormControlLabel,
  Tab,
  Tabs,
  TextField,
  Tooltip,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import StarIcon from "@mui/icons-material/Star";
import UploadIcon from "@mui/icons-material/Upload";
import DrawIcon from "@mui/icons-material/Draw";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import { selectUser } from "../../features/userSlice";
import { PageHeader, PageShell } from "../../components/UI/Page";
import { EmptyState, LoadingState } from "../../components/UI/States";

// Emails are read outside the app, so image URLs baked into signatures must
// be absolute against prod no matter where the admin UI is running.
const PROD_BASE = "https://node.bigclumber.com";

/**
 * Admin console for the Outlook signature system.
 *
 * Templates hold HTML with {{placeholder}} tokens (and {{#if field}} blocks)
 * filled from each employee's live Entra ID profile. Group assignments map
 * Entra ID groups to templates — a user picks up the highest-priority
 * mapping their AD groups match, falling back to the default template.
 */

const STARTER_TEMPLATE = `<table cellpadding="0" cellspacing="0" style="font-family: Arial, sans-serif; font-size: 12px; color: #222222;">
  <tr>
    <td style="padding-right: 14px; border-right: 3px solid #1e6b3a; vertical-align: top;">
      <strong style="font-size: 14px; color: #1e6b3a;">{{displayName}}</strong><br/>
      {{#if jobTitle}}{{jobTitle}}<br/>{{/if}}
      Big C Lumber{{#if officeLocation}} &ndash; {{officeLocation}}{{/if}}
    </td>
    <td style="padding-left: 14px; vertical-align: top;">
      {{#if businessPhone}}O: {{businessPhone}}<br/>{{/if}}
      {{#if mobilePhone}}M: {{mobilePhone}}<br/>{{/if}}
      <a href="mailto:{{mail}}" style="color: #1e6b3a;">{{mail}}</a><br/>
      <a href="https://www.bigclumber.com" style="color: #1e6b3a;">bigclumber.com</a>
    </td>
  </tr>
</table>`;

// Name fields an admin can override per employee. Keys match the server's
// NAME_OVERRIDE_FIELDS; labels match the template placeholders.
const NAME_FIELDS = [
  { key: "displayName", label: "Full name" },
  { key: "givenName", label: "First name" },
  { key: "surname", label: "Last name" },
];

const directoryNames = (u) => ({
  displayName: u.displayName || "",
  givenName: u.givenName || "",
  surname: u.surname || "",
});

const jsonOrThrow = async (res) => {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
};

/*
The preview body stays white in every theme on purpose: this renders the exact
email-signature HTML that recipients will see on a white message background, so
darkening (or glassing) it would misrepresent the result. Only the frame's
border follows the theme.
*/
const PreviewFrame = ({ html, minHeight = 140 }) => (
  <iframe
    title="Signature preview"
    sandbox=""
    srcDoc={`<body style="margin:12px;background:#ffffff;">${html || ""}</body>`}
    className="app-light-surface block w-full rounded-xl border border-slate-300 bg-white dark:border-slate-600"
    style={{ minHeight }}
  />
);

const SignatureManager = () => {
  const user = useSelector(selectUser);

  /*
  This page is almost entirely MUI (Tabs, Dialog, Select, TextField). No local
  createTheme here on purpose: MuiThemeBridge is mounted app-wide in index.js, so
  these already resolve from the dark palette. A nested provider would replace it
  for this subtree and drop its slate surfaces.
  */

  const [tab, setTab] = useState(0);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  // shared data
  const [templates, setTemplates] = useState([]);
  const [defaultTemplateId, setDefaultTemplateId] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [nameOverrides, setNameOverrides] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);

  const flash = (msg) => {
    setNotice(msg);
    setError(null);
  };
  const fail = (err) => {
    setError(err.message || String(err));
    setNotice(null);
  };

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      const [tpl, maps, ovr, names, ph, assetList] = await Promise.all([
        fetch("/api/signatures/templates").then(jsonOrThrow),
        fetch("/api/signatures/mappings").then(jsonOrThrow),
        fetch("/api/signatures/overrides").then(jsonOrThrow),
        fetch("/api/signatures/name-overrides").then(jsonOrThrow),
        fetch("/api/signatures/placeholders").then(jsonOrThrow),
        fetch("/api/signatures/assets").then(jsonOrThrow),
      ]);
      setTemplates(tpl.templates);
      setDefaultTemplateId(tpl.defaultTemplateId);
      setMappings(maps);
      setOverrides(ovr);
      setNameOverrides(names);
      setPlaceholders(ph);
      setAssets(assetList);
    } catch (err) {
      fail(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // ------------------------------------------------------------ templates

  const emptyDraft = {
    _id: null,
    name: "",
    html: STARTER_TEMPLATE,
    applyToReplies: true,
  };
  const [draft, setDraft] = useState(null); // null = editor closed
  const [saving, setSaving] = useState(false);
  const [livePreview, setLivePreview] = useState("");
  const htmlRef = useRef(null);

  // Debounced live preview against sample data while editing.
  useEffect(() => {
    if (!draft) return undefined;
    const timer = setTimeout(async () => {
      try {
        const data = await fetch("/api/signatures/preview", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ html: draft.html }),
        }).then(jsonOrThrow);
        setLivePreview(data.html);
      } catch (err) {
        setLivePreview(
          `<span style="color:#b00020;">Preview failed: ${err.message}</span>`,
        );
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [draft]);

  const insertPlaceholder = (token) => {
    const snippet = `{{${token}}}`;
    const el = htmlRef.current;
    setDraft((d) => {
      if (!d) return d;
      if (el && typeof el.selectionStart === "number") {
        const at = el.selectionStart;
        return {
          ...d,
          html: d.html.slice(0, at) + snippet + d.html.slice(el.selectionEnd),
        };
      }
      return { ...d, html: d.html + snippet };
    });
  };

  const insertImage = (asset) => {
    const snippet = `<img src="${PROD_BASE}${asset.url}" alt="" style="border: 0;" />`;
    const el = htmlRef.current;
    setDraft((d) => {
      if (!d) return d;
      if (el && typeof el.selectionStart === "number") {
        const at = el.selectionStart;
        return {
          ...d,
          html: d.html.slice(0, at) + snippet + d.html.slice(el.selectionEnd),
        };
      }
      return { ...d, html: d.html + snippet };
    });
  };

  const saveTemplate = async () => {
    try {
      setSaving(true);
      const payload = {
        name: draft.name.trim(),
        html: draft.html,
        applyToReplies: draft.applyToReplies !== false,
        updatedBy: user?.email || user?.name || null,
      };
      if (!payload.name) throw new Error("Template needs a name");

      const url = draft._id
        ? `/api/signatures/templates/${draft._id}`
        : "/api/signatures/templates";
      await fetch(url, {
        method: draft._id ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }).then(jsonOrThrow);

      setDraft(null);
      flash(`Saved "${payload.name}"`);
      loadAll();
    } catch (err) {
      fail(err);
    } finally {
      setSaving(false);
    }
  };

  const duplicateTemplate = async (template) => {
    try {
      await fetch("/api/signatures/templates", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: `${template.name} (copy)`,
          html: template.html,
          applyToReplies: template.applyToReplies !== false,
          updatedBy: user?.email || user?.name || null,
        }),
      }).then(jsonOrThrow);
      flash(`Duplicated "${template.name}"`);
      loadAll();
    } catch (err) {
      fail(err);
    }
  };

  const deleteTemplate = async (template) => {
    if (!window.confirm(`Delete template "${template.name}"?`)) return;
    try {
      await fetch(`/api/signatures/templates/${template._id}`, {
        method: "DELETE",
      }).then(jsonOrThrow);
      flash(`Deleted "${template.name}"`);
      loadAll();
    } catch (err) {
      fail(err);
    }
  };

  // --------------------------------------------------------------- assets

  const [uploading, setUploading] = useState(false);

  const uploadAsset = (file) => {
    if (!file) return;
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await fetch("/api/signatures/assets", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: file.name, dataUrl: reader.result }),
        }).then(jsonOrThrow);
        flash(`Uploaded ${file.name}`);
        loadAll();
      } catch (err) {
        fail(err);
      } finally {
        setUploading(false);
      }
    };
    reader.onerror = () => {
      fail(new Error("Could not read that file"));
      setUploading(false);
    };
    reader.readAsDataURL(file);
  };

  const deleteAsset = async (asset) => {
    if (
      !window.confirm(
        `Delete ${asset.name}? Any signature already using it will show a broken image.`,
      )
    )
      return;
    try {
      await fetch(`/api/signatures/assets/${asset.name}`, {
        method: "DELETE",
      }).then(jsonOrThrow);
      flash(`Deleted ${asset.name}`);
      loadAll();
    } catch (err) {
      fail(err);
    }
  };

  const copyAssetUrl = async (asset) => {
    try {
      await navigator.clipboard.writeText(PROD_BASE + asset.url);
      flash(`Copied URL for ${asset.name}`);
    } catch (err) {
      fail(new Error("Clipboard unavailable — copy from the address instead"));
    }
  };

  // ------------------------------------------------------------- mappings

  const [groupQuery, setGroupQuery] = useState("");
  const [groupOptions, setGroupOptions] = useState([]);
  const [groupSearching, setGroupSearching] = useState(false);
  const [newGroup, setNewGroup] = useState(null);
  const [newGroupTemplate, setNewGroupTemplate] = useState("");

  useEffect(() => {
    if (tab !== 2) return undefined;
    const timer = setTimeout(async () => {
      try {
        setGroupSearching(true);
        const data = await fetch(
          `/api/signatures/groups/search?q=${encodeURIComponent(groupQuery)}`,
        ).then(jsonOrThrow);
        setGroupOptions(data);
      } catch (err) {
        setGroupOptions([]);
        fail(err);
      } finally {
        setGroupSearching(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [groupQuery, tab]);

  const addMapping = async () => {
    try {
      if (!newGroup) throw new Error("Pick an Entra ID group");
      if (!newGroupTemplate) throw new Error("Pick a template for the group");
      await fetch("/api/signatures/mappings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          groupId: newGroup.id,
          groupName: newGroup.displayName,
          templateId: newGroupTemplate,
        }),
      }).then(jsonOrThrow);
      setNewGroup(null);
      setNewGroupTemplate("");
      flash(`Added mapping for ${newGroup.displayName}`);
      loadAll();
    } catch (err) {
      fail(err);
    }
  };

  const updateMapping = async (mapping, update) => {
    try {
      await fetch(`/api/signatures/mappings/${mapping._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(update),
      }).then(jsonOrThrow);
      loadAll();
    } catch (err) {
      fail(err);
    }
  };

  const moveMapping = (index, delta) => {
    // Swap priorities with the neighbor so the order stays dense.
    const sorted = [...mappings].sort((a, b) => a.priority - b.priority);
    const target = sorted[index + delta];
    if (!target) return;
    const current = sorted[index];
    updateMapping(current, { priority: target.priority });
    updateMapping(target, { priority: current.priority });
  };

  const deleteMapping = async (mapping) => {
    try {
      await fetch(`/api/signatures/mappings/${mapping._id}`, {
        method: "DELETE",
      }).then(jsonOrThrow);
      flash(`Removed mapping for ${mapping.groupName || mapping.groupId}`);
      loadAll();
    } catch (err) {
      fail(err);
    }
  };

  // ---------------------------------------------------- per-user overrides

  const [userQuery, setUserQuery] = useState("");
  const [userOptions, setUserOptions] = useState([]);
  const [userSearching, setUserSearching] = useState(false);
  const [newUser, setNewUser] = useState(null);
  const [newUserTemplate, setNewUserTemplate] = useState("");

  useEffect(() => {
    if (tab !== 2) return undefined;
    const timer = setTimeout(async () => {
      try {
        setUserSearching(true);
        const data = await fetch(
          `/api/signatures/users/search?q=${encodeURIComponent(userQuery)}`,
        ).then(jsonOrThrow);
        setUserOptions(data);
      } catch (err) {
        setUserOptions([]);
        fail(err);
      } finally {
        setUserSearching(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [userQuery, tab]);

  const addOverride = async () => {
    try {
      if (!newUser) throw new Error("Pick an employee");
      if (!newUserTemplate) throw new Error("Pick a template for them");
      await fetch("/api/signatures/overrides", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: newUser.mail,
          userName: newUser.displayName,
          templateId: newUserTemplate,
        }),
      }).then(jsonOrThrow);
      setNewUser(null);
      setNewUserTemplate("");
      flash(`Assigned a signature to ${newUser.displayName}`);
      loadAll();
    } catch (err) {
      fail(err);
    }
  };

  const updateOverride = async (override, templateId) => {
    try {
      await fetch(`/api/signatures/overrides/${override._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ templateId }),
      }).then(jsonOrThrow);
      loadAll();
    } catch (err) {
      fail(err);
    }
  };

  const deleteOverride = async (override) => {
    try {
      await fetch(`/api/signatures/overrides/${override._id}`, {
        method: "DELETE",
      }).then(jsonOrThrow);
      flash(
        `Removed override for ${override.userName || override.userKey} — they follow group/default rules again`,
      );
      loadAll();
    } catch (err) {
      fail(err);
    }
  };

  // ------------------------------------------------------- name overrides

  // null = dialog closed. `directory` holds the employee's Entra ID names so
  // the dialog can show what a blank field falls back to.
  const emptyNameDraft = {
    editing: false,
    picked: null,
    email: "",
    userName: "",
    directory: null,
    displayName: "",
    givenName: "",
    surname: "",
  };
  const [nameDraft, setNameDraft] = useState(null);
  const [nameSaving, setNameSaving] = useState(false);
  const [nameError, setNameError] = useState(null);
  const [nameUserQuery, setNameUserQuery] = useState("");
  const [nameUserOptions, setNameUserOptions] = useState([]);
  const [nameUserSearching, setNameUserSearching] = useState(false);

  const nameSearchOpen = Boolean(nameDraft) && !nameDraft.editing;
  useEffect(() => {
    if (!nameSearchOpen) return undefined;
    const timer = setTimeout(async () => {
      try {
        setNameUserSearching(true);
        const data = await fetch(
          `/api/signatures/users/search?q=${encodeURIComponent(nameUserQuery)}`,
        ).then(jsonOrThrow);
        setNameUserOptions(data);
      } catch (err) {
        setNameUserOptions([]);
        setNameError(err.message || String(err));
      } finally {
        setNameUserSearching(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [nameUserQuery, nameSearchOpen]);

  const openNewNameOverride = () => {
    setNameError(null);
    setNameUserQuery("");
    setNameUserOptions([]);
    setNameDraft(emptyNameDraft);
  };

  const openEditNameOverride = async (override) => {
    setNameError(null);
    setNameDraft({
      ...emptyNameDraft,
      editing: true,
      email: override.userKey,
      userName: override.userName || "",
      displayName: override.displayName || "",
      givenName: override.givenName || "",
      surname: override.surname || "",
    });
    // Look up their current directory names for the field hints. Only a
    // hint, so a failed lookup just leaves them off.
    try {
      const matches = await fetch(
        `/api/signatures/users/search?q=${encodeURIComponent(override.userKey)}`,
      ).then(jsonOrThrow);
      const match = matches.find((u) =>
        [u.mail, u.userPrincipalName].some(
          (a) => String(a || "").toLowerCase() === override.userKey,
        ),
      );
      if (match) {
        setNameDraft((d) =>
          d && d.email === override.userKey
            ? {
                ...d,
                userName: match.displayName || d.userName,
                directory: directoryNames(match),
              }
            : d,
        );
      }
    } catch (err) {
      // hints only
    }
  };

  // Picking someone who already has an override loads it, so saving edits
  // it instead of silently replacing it with blanks.
  const pickNameUser = (picked) => {
    const existing =
      picked &&
      nameOverrides.find(
        (o) => o.userKey === String(picked.mail || "").toLowerCase(),
      );
    setNameDraft((d) => ({
      ...d,
      picked,
      email: picked?.mail || "",
      userName: picked?.displayName || "",
      directory: picked ? directoryNames(picked) : null,
      displayName: existing?.displayName || "",
      givenName: existing?.givenName || "",
      surname: existing?.surname || "",
    }));
  };

  const saveNameOverride = async () => {
    try {
      if (!nameDraft.email) throw new Error("Pick an employee");
      setNameSaving(true);
      await fetch("/api/signatures/name-overrides", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: nameDraft.email,
          userName: nameDraft.userName || null,
          displayName: nameDraft.displayName.trim(),
          givenName: nameDraft.givenName.trim(),
          surname: nameDraft.surname.trim(),
          updatedBy: user?.email || user?.name || null,
        }),
      }).then(jsonOrThrow);
      flash(
        `Saved the signature name for ${nameDraft.userName || nameDraft.email}`,
      );
      setNameDraft(null);
      loadAll();
    } catch (err) {
      setNameError(err.message || String(err));
    } finally {
      setNameSaving(false);
    }
  };

  const deleteNameOverride = async (override) => {
    try {
      await fetch(`/api/signatures/name-overrides/${override._id}`, {
        method: "DELETE",
      }).then(jsonOrThrow);
      flash(
        `Removed the name override for ${override.userName || override.userKey} — their signature uses the directory name again`,
      );
      loadAll();
    } catch (err) {
      fail(err);
    }
  };

  const setDefault = async (templateId) => {
    try {
      await fetch("/api/signatures/settings/default", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ templateId: templateId || null }),
      }).then(jsonOrThrow);
      setDefaultTemplateId(templateId || null);
      flash("Default signature updated");
    } catch (err) {
      fail(err);
    }
  };

  // -------------------------------------------------------------- preview

  const [previewEmail, setPreviewEmail] = useState("");
  const [previewResult, setPreviewResult] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const renderForUser = async (email = previewEmail) => {
    try {
      setPreviewLoading(true);
      setPreviewResult(null);
      const data = await fetch(
        `/api/signatures/render?email=${encodeURIComponent(email.trim())}`,
      ).then(jsonOrThrow);
      setPreviewResult(data);
      setError(null);
    } catch (err) {
      fail(err);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Jump to Preview As User and render this employee right away.
  const previewUser = (email) => {
    setPreviewEmail(email);
    setTab(3);
    renderForUser(email);
  };

  // ---------------------------------------------------------------- render

  const templateName = (id) =>
    templates.find((t) => String(t._id) === String(id))?.name || "(missing)";

  const sortedMappings = [...mappings].sort((a, b) => a.priority - b.priority);

  return (
    <PageShell size="xl">
      <PageHeader
        icon={DrawIcon}
        tone="slate"
        eyebrow="Admin"
        eyebrowTo="/admin"
        title="Signature Manager"
        description="Manage company email signatures, and choose which Entra ID groups get which signature. Employee details fill in automatically from the directory."
      />

      {error && (
        <Alert severity="error" onClose={() => setError(null)} className="mb-4">
          {error}
        </Alert>
      )}
      {notice && (
        <Alert
          severity="success"
          onClose={() => setNotice(null)}
          className="mb-4"
        >
          {notice}
        </Alert>
      )}

      <div className="app-card overflow-hidden rounded-2xl border">
        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          className="app-divider border-b px-2"
        >
          <Tab label="Templates" />
          <Tab label="Images" />
          <Tab label="Assignments" />
          <Tab label="Preview As User" />
        </Tabs>

        {loading ? (
          <LoadingState label="Loading signatures…" />
        ) : (
          <div className="p-4 sm:p-5">
            {/* ------------------------------------------------ Templates */}
            {tab === 0 && (
              <div>
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                  <span className="app-muted text-sm tabular-nums">
                    {templates.length} template
                    {templates.length === 1 ? "" : "s"}
                  </span>
                  <Button
                    variant="contained"
                    onClick={() => setDraft({ ...emptyDraft })}
                  >
                    New Template
                  </Button>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  {templates.map((t) => (
                    <div
                      key={t._id}
                      className="app-surface flex flex-col rounded-2xl border p-4"
                    >
                      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                        <div className="flex min-w-0 flex-wrap items-center gap-2 font-semibold">
                          {t.name}
                          {String(defaultTemplateId) === String(t._id) && (
                            <Chip
                              size="small"
                              color="success"
                              icon={<StarIcon />}
                              label="Default"
                            />
                          )}
                          {t.applyToReplies === false && (
                            <Chip
                              size="small"
                              variant="outlined"
                              label="New messages only"
                            />
                          )}
                        </div>
                        <div className="flex shrink-0 items-center gap-0.5">
                          <Button size="small" onClick={() => setDraft({ ...t })}>
                            Edit
                          </Button>
                          <Tooltip title="Duplicate template">
                            <IconButton
                              size="small"
                              onClick={() => duplicateTemplate(t)}
                              aria-label={`Duplicate ${t.name}`}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <IconButton
                            size="small"
                            onClick={() => deleteTemplate(t)}
                            aria-label={`Delete ${t.name}`}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </div>
                      </div>
                      <PreviewFrame html={t.html} minHeight={110} />
                      <div className="app-muted mt-2 text-xs">
                        Updated{" "}
                        {t.updatedAt
                          ? new Date(t.updatedAt).toLocaleString()
                          : "—"}
                        {t.updatedBy ? ` by ${t.updatedBy}` : ""}
                      </div>
                    </div>
                  ))}
                  {!templates.length && (
                    <div className="md:col-span-2">
                      <EmptyState
                        compact
                        icon={ArticleOutlinedIcon}
                        title="No templates yet"
                        description="Use New Template to create the first one."
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* -------------------------------------------------- Images */}
            {tab === 1 && (
              <div>
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <span className="app-muted max-w-2xl text-sm">
                    Images for use in signatures — logos, banners, social
                    icons. Uploads are hosted at a public URL that email
                    clients can load.
                  </span>
                  <Button
                    variant="contained"
                    component="label"
                    startIcon={<UploadIcon />}
                    disabled={uploading}
                  >
                    {uploading ? "Uploading…" : "Upload Image"}
                    <input
                      hidden
                      type="file"
                      accept="image/png,image/jpeg,image/gif"
                      onChange={(e) => {
                        uploadAsset(e.target.files[0]);
                        e.target.value = "";
                      }}
                    />
                  </Button>
                </div>

                <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                  {assets.map((a) => (
                    <div
                      key={a.name}
                      className="app-surface rounded-2xl border p-3"
                    >
                      <div
                        className="mb-2 flex h-24 items-center justify-center overflow-hidden rounded-xl"
                        style={{
                          background:
                            "repeating-conic-gradient(#e5e7eb 0% 25%, #ffffff 0% 50%) 50% / 16px 16px",
                        }}
                      >
                        <img
                          src={a.url}
                          alt={a.name}
                          className="max-h-24 max-w-full"
                        />
                      </div>
                      <div className="truncate text-sm font-medium" title={a.name}>
                        {a.name}
                      </div>
                      <div className="app-muted mb-1 text-xs">
                        {(a.size / 1024).toFixed(1)} KB
                      </div>
                      <div className="flex items-center justify-between">
                        <Tooltip title="Copy public URL">
                          <IconButton
                            size="small"
                            onClick={() => copyAssetUrl(a)}
                            aria-label={`Copy public URL for ${a.name}`}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <IconButton
                          size="small"
                          onClick={() => deleteAsset(a)}
                          aria-label={`Delete ${a.name}`}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </div>
                    </div>
                  ))}
                  {!assets.length && (
                    <div className="col-span-full">
                      <EmptyState
                        compact
                        icon={ImageOutlinedIcon}
                        title="No images uploaded yet"
                        description="The add-in's built-in images (logo, social icons) are always available under /signature-addin/assets/."
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ----------------------------------------- Group assignments */}
            {tab === 2 && (
              <div className="space-y-6">
                <p className="app-muted text-sm">
                  Signatures resolve top to bottom: a per-user assignment
                  wins over group mappings, and the default covers everyone
                  else.
                </p>

                <section className="app-surface rounded-2xl border p-4 sm:p-5">
                  <h2 className="font-semibold">Per-user assignments</h2>
                  <p className="app-muted mb-3 mt-0.5 text-sm">
                    Give a specific employee a signature regardless of their
                    groups.
                  </p>

                  {overrides.map((o) => (
                    <div
                      key={o._id}
                      className="app-card mb-2 flex flex-wrap items-center gap-2 rounded-xl border px-3 py-2"
                    >
                      <span className="min-w-40 flex-1">
                        <span className="font-medium">
                          {o.userName || o.userKey}
                        </span>{" "}
                        <span className="app-muted text-xs">{o.userKey}</span>
                      </span>
                      <FormControl size="small" className="w-full sm:w-56">
                        <Select
                          value={String(o.templateId)}
                          inputProps={{
                            "aria-label": `Template for ${o.userName || o.userKey}`,
                          }}
                          onChange={(e) => updateOverride(o, e.target.value)}
                        >
                          {templates.map((t) => (
                            <MenuItem key={t._id} value={String(t._id)}>
                              {t.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <IconButton
                        size="small"
                        onClick={() => deleteOverride(o)}
                        aria-label="Remove user assignment"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </div>
                  ))}
                  {!overrides.length && (
                    <p className="app-muted py-3 text-center text-sm">
                      No per-user assignments.
                    </p>
                  )}

                  <div className="app-divider mt-3 flex flex-wrap items-center gap-2 border-t pt-4">
                    <Autocomplete
                      size="small"
                      className="w-full sm:w-72"
                      options={userOptions}
                      loading={userSearching}
                      value={newUser}
                      onChange={(e, v) => setNewUser(v)}
                      onInputChange={(e, v) => setUserQuery(v)}
                      getOptionLabel={(o) =>
                        o.displayName ? `${o.displayName} (${o.mail})` : ""
                      }
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      filterOptions={(x) => x}
                      renderInput={(params) => (
                        <TextField {...params} label="Search employees" />
                      )}
                    />
                    <FormControl size="small" className="w-full sm:w-56">
                      <InputLabel id="new-override-template">Template</InputLabel>
                      <Select
                        labelId="new-override-template"
                        label="Template"
                        value={newUserTemplate}
                        onChange={(e) => setNewUserTemplate(e.target.value)}
                      >
                        {templates.map((t) => (
                          <MenuItem key={t._id} value={String(t._id)}>
                            {t.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button variant="contained" onClick={addOverride}>
                      Assign
                    </Button>
                  </div>
                </section>

                <section className="app-surface rounded-2xl border p-4 sm:p-5">
                  <h2 className="font-semibold">Group mappings</h2>
                  <p className="app-muted mb-3 mt-0.5 text-sm">
                    Employees get the first mapping (top to bottom) that
                    matches one of their Entra ID groups — including groups
                    inherited through nesting.
                  </p>

                  {sortedMappings.map((m, i) => (
                    <div
                      key={m._id}
                      className="app-card mb-2 flex flex-wrap items-center gap-2 rounded-xl border px-3 py-2"
                    >
                      <span className="app-chip app-muted tabular-nums">
                        {i + 1}
                      </span>
                      <span className="min-w-40 flex-1 font-medium">
                        {m.groupName || m.groupId}
                      </span>
                      <FormControl size="small" className="w-full sm:w-56">
                        <Select
                          value={String(m.templateId)}
                          inputProps={{
                            "aria-label": `Template for ${m.groupName || m.groupId}`,
                          }}
                          onChange={(e) =>
                            updateMapping(m, { templateId: e.target.value })
                          }
                        >
                          {templates.map((t) => (
                            <MenuItem key={t._id} value={String(t._id)}>
                              {t.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Tooltip title="Higher priority">
                        <span>
                          <IconButton
                            size="small"
                            disabled={i === 0}
                            onClick={() => moveMapping(i, -1)}
                            aria-label="Higher priority"
                          >
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Lower priority">
                        <span>
                          <IconButton
                            size="small"
                            disabled={i === sortedMappings.length - 1}
                            onClick={() => moveMapping(i, 1)}
                            aria-label="Lower priority"
                          >
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <IconButton
                        size="small"
                        onClick={() => deleteMapping(m)}
                        aria-label="Remove mapping"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </div>
                  ))}
                  {!sortedMappings.length && (
                    <p className="app-muted py-3 text-center text-sm">
                      No group mappings yet — everyone gets the default
                      signature.
                    </p>
                  )}

                  <div className="app-divider mt-3 flex flex-wrap items-center gap-2 border-t pt-4">
                    <Autocomplete
                      size="small"
                      className="w-full sm:w-72"
                      options={groupOptions}
                      loading={groupSearching}
                      value={newGroup}
                      onChange={(e, v) => setNewGroup(v)}
                      onInputChange={(e, v) => setGroupQuery(v)}
                      getOptionLabel={(o) => o.displayName || ""}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      filterOptions={(x) => x}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Search Entra ID groups"
                        />
                      )}
                    />
                    <FormControl size="small" className="w-full sm:w-56">
                      <InputLabel id="new-mapping-template">Template</InputLabel>
                      <Select
                        labelId="new-mapping-template"
                        label="Template"
                        value={newGroupTemplate}
                        onChange={(e) => setNewGroupTemplate(e.target.value)}
                      >
                        {templates.map((t) => (
                          <MenuItem key={t._id} value={String(t._id)}>
                            {t.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button variant="contained" onClick={addMapping}>
                      Add Mapping
                    </Button>
                  </div>
                </section>

                <section className="app-surface rounded-2xl border p-4 sm:p-5">
                  <h2 className="font-semibold">Default signature</h2>
                  <p className="app-muted mb-3 mt-0.5 text-sm">
                    Used for anyone with no per-user assignment and no
                    matching group mapping.
                  </p>
                  <FormControl size="small" className="w-full sm:w-72">
                    <InputLabel id="default-template-label">
                      Default template
                    </InputLabel>
                    <Select
                      labelId="default-template-label"
                      label="Default template"
                      value={defaultTemplateId || ""}
                      onChange={(e) => setDefault(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>None (no signature)</em>
                      </MenuItem>
                      {templates.map((t) => (
                        <MenuItem key={t._id} value={String(t._id)}>
                          {t.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </section>

                <section className="app-surface rounded-2xl border p-4 sm:p-5">
                  <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <h2 className="font-semibold">Name overrides</h2>
                      <p className="app-muted mt-0.5 text-sm">
                        Show a different name than Entra ID in one employee's
                        signature, for example after a name change that
                        hasn't been made in AD. Their template and every
                        other detail still come from the directory.
                      </p>
                    </div>
                    <Button variant="contained" onClick={openNewNameOverride}>
                      Add Name Override
                    </Button>
                  </div>

                  {nameOverrides.map((o) => (
                    <div
                      key={o._id}
                      className="app-card mb-2 flex flex-wrap items-center gap-2 rounded-xl border px-3 py-2"
                    >
                      <span className="min-w-40 flex-1">
                        <span className="font-medium">
                          {o.userName || o.userKey}
                        </span>{" "}
                        <span className="app-muted text-xs">{o.userKey}</span>
                      </span>
                      <span className="flex flex-wrap gap-1">
                        {NAME_FIELDS.filter((f) => o[f.key]).map((f) => (
                          <span key={f.key} className="app-chip">
                            {f.label}: {o[f.key]}
                          </span>
                        ))}
                      </span>
                      <Tooltip title="Preview their signature">
                        <IconButton
                          size="small"
                          onClick={() => previewUser(o.userKey)}
                          aria-label={`Preview signature for ${o.userName || o.userKey}`}
                        >
                          <VisibilityOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <IconButton
                        size="small"
                        onClick={() => openEditNameOverride(o)}
                        aria-label={`Edit name override for ${o.userName || o.userKey}`}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => deleteNameOverride(o)}
                        aria-label="Remove name override"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </div>
                  ))}
                  {!nameOverrides.length && (
                    <p className="app-muted py-3 text-center text-sm">
                      No name overrides. Every signature uses the directory
                      name.
                    </p>
                  )}
                </section>
              </div>
            )}

            {/* ------------------------------------------------- Preview */}
            {tab === 3 && (
              <div>
                <p className="app-muted mb-3 text-sm">
                  See exactly what a specific employee's signature renders as,
                  using their live directory profile.
                </p>
                <div className="mb-4 flex flex-wrap items-center gap-2">
                  <TextField
                    size="small"
                    label="Employee email"
                    className="w-full sm:w-80"
                    value={previewEmail}
                    onChange={(e) => setPreviewEmail(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && renderForUser()}
                  />
                  <Button
                    variant="contained"
                    onClick={() => renderForUser()}
                    disabled={!previewEmail.trim() || previewLoading}
                  >
                    {previewLoading ? "Rendering…" : "Render"}
                  </Button>
                </div>
                {previewResult && (
                  <div className="app-surface rounded-2xl border p-4">
                    <p className="app-muted mb-3 text-sm">
                      Matched template:{" "}
                      <strong>{previewResult.templateName}</strong>
                      {previewResult.nameOverridden &&
                        " · Name comes from a name override (Assignments tab), not Entra ID"}
                    </p>
                    <PreviewFrame html={previewResult.html} />
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ------------------------------------------------- Template editor */}
      <Dialog
        open={Boolean(draft)}
        onClose={() => setDraft(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {draft?._id ? "Edit Template" : "New Template"}
        </DialogTitle>
        <DialogContent dividers>
          {draft && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-4">
                <TextField
                  label="Template name"
                  size="small"
                  className="min-w-0 flex-1 sm:min-w-64"
                  value={draft.name}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, name: e.target.value }))
                  }
                />
                <Tooltip title="When off, this signature is only inserted on brand-new messages and meeting invites. Users can override this for their own mailbox from the add-in taskpane.">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={draft.applyToReplies !== false}
                        onChange={(e) =>
                          setDraft((d) => ({
                            ...d,
                            applyToReplies: e.target.checked,
                          }))
                        }
                      />
                    }
                    label="Apply to replies & forwards"
                  />
                </Tooltip>
              </div>
              <div>
                <p className="app-muted mb-1 text-xs">
                  Click to insert a field where your cursor is — it fills in
                  from each employee's directory profile. Wrap optional lines
                  in {"{{#if field}} ... {{/if}}"} so they disappear when the
                  employee has no value.
                </p>
                <div className="flex flex-wrap gap-1">
                  {placeholders.map((p) => (
                    <Tooltip key={p.token} title={`e.g. ${p.example}`}>
                      <Chip
                        size="small"
                        label={p.label}
                        onClick={() => insertPlaceholder(p.token)}
                      />
                    </Tooltip>
                  ))}
                </div>
              </div>
              {assets.length > 0 && (
                <div>
                  <p className="app-muted mb-1 text-xs">
                    Click an image to insert it (uploaded on the Images tab).
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    {assets.map((a) => (
                      <Tooltip key={a.name} title={a.name}>
                        <button
                          type="button"
                          onClick={() => insertImage(a)}
                          aria-label={`Insert ${a.name}`}
                          className="app-surface rounded-lg border p-1 transition hover:border-sky-400"
                        >
                          <img src={a.url} alt={a.name} className="h-8" />
                        </button>
                      </Tooltip>
                    ))}
                  </div>
                </div>
              )}
              <div className="grid gap-4 lg:grid-cols-2">
                <TextField
                  inputRef={htmlRef}
                  label="Signature HTML"
                  multiline
                  minRows={16}
                  fullWidth
                  value={draft.html}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, html: e.target.value }))
                  }
                  InputProps={{
                    className: "font-mono text-sm",
                  }}
                />
                <div>
                  <p className="app-muted mb-1 text-xs">
                    Live preview (sample data)
                  </p>
                  <PreviewFrame html={livePreview} minHeight={320} />
                </div>
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDraft(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveTemplate} disabled={saving}>
            {saving ? "Saving…" : "Save Template"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* -------------------------------------------- Name override editor */}
      <Dialog
        open={Boolean(nameDraft)}
        onClose={() => setNameDraft(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {nameDraft?.editing ? "Edit Name Override" : "New Name Override"}
        </DialogTitle>
        <DialogContent dividers>
          {nameDraft && (
            <div className="space-y-4">
              {nameError && (
                <Alert severity="error" onClose={() => setNameError(null)}>
                  {nameError}
                </Alert>
              )}
              {nameDraft.editing ? (
                <p className="text-sm">
                  <span className="font-medium">
                    {nameDraft.userName || nameDraft.email}
                  </span>{" "}
                  <span className="app-muted text-xs">{nameDraft.email}</span>
                </p>
              ) : (
                <Autocomplete
                  size="small"
                  options={nameUserOptions}
                  loading={nameUserSearching}
                  value={nameDraft.picked}
                  onChange={(e, v) => pickNameUser(v)}
                  onInputChange={(e, v) => setNameUserQuery(v)}
                  getOptionLabel={(o) =>
                    o.displayName ? `${o.displayName} (${o.mail})` : ""
                  }
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  filterOptions={(x) => x}
                  renderInput={(params) => (
                    <TextField {...params} label="Search employees" />
                  )}
                />
              )}
              <p className="app-muted text-xs">
                Fill in only the names that should change. A blank field keeps
                the Entra ID value.
              </p>
              {NAME_FIELDS.map((f) => (
                <TextField
                  key={f.key}
                  label={f.label}
                  size="small"
                  fullWidth
                  value={nameDraft[f.key]}
                  onChange={(e) =>
                    setNameDraft((d) => ({ ...d, [f.key]: e.target.value }))
                  }
                  helperText={
                    nameDraft.directory
                      ? `In Entra ID: ${nameDraft.directory[f.key] || "(blank)"}`
                      : " "
                  }
                />
              ))}
              <p className="app-muted text-xs">
                Outlook picks up the change on the employee's next signature
                refresh: within 6 hours, or right away with Refresh from server
                in the add-in.
              </p>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNameDraft(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={saveNameOverride}
            disabled={nameSaving}
          >
            {nameSaving ? "Saving…" : "Save Override"}
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default SignatureManager;
